import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

/**
 * API 轮询管理器
 * 用于管理接口的初始化、轮询、错误处理等功能
 * 
 * @class ApiPollingManager
 * 
 * @description
 * 该类提供了完整的 API 接口管理功能，包括：
 * - 初始化只需加载一次的数据
 * - 初始化数据（首次加载）
 * - 设置/移除页面可见性监听
 * - 处理页面可见性变化
 * - 启动/停止实时轮询
 * - 启动/停止定时轮询
 * - 执行单个接口调用
 * - 清除所有定时器
 * - 设置/清理错误监听
 * - 检查 Redux state 中的错误
 * - 处理接口错误（弹出确认框）
 * - 获取数据（手动触发）
 * 
 * @methods
 * 
 * @method constructor(apiConfig, componentInstance)
 * 构造函数，初始化管理器
 * @param {Object} apiConfig - API 配置对象，包含 once、realtime、polling、manual 四个数组
 * @param {Object} componentInstance - React 组件实例
 * 
 * @method init(defaultQuery)
 * 初始化管理器（在 componentDidMount 中调用）
 * 执行以下操作：
 * - 初始化只需加载一次的数据
 * - 初始化数据（首次加载）
 * - 监听页面可见性变化
 * - 启动实时轮询
 * - 启动定时轮询
 * - 设置错误监听
 * @param {Object} defaultQuery - 默认查询参数，默认为空对象
 * 
 * @method updateQuery(query)
 * 更新查询参数
 * @param {Object} query - 新的查询参数
 * 
 * @method cleanup()
 * 清理管理器（在 componentWillUnmount 中调用）
 * 执行以下操作：
 * - 清理所有定时器
 * - 移除页面可见性监听
 * - 清理错误监听
 * 
 * @method checkReduxErrors(prevProps, getErrorProps)
 * 检查 Redux state 中的错误（在 componentDidUpdate 中调用）
 * @param {Object} prevProps - 上一次的 props
 * @param {Function} getErrorProps - 获取错误 props 的函数，返回 { [configName]: errorObj }
 * @example
 * this.apiManager.checkReduxErrors(prevProps, (props) => {
 *   return {
 *     fetchDBSummary: props.dbSummaryObj,
 *     fetchProductSummary: props.productSummaryObj,
 *   };
 * });
 * 
 * @method setupErrorListener()
 * 设置错误监听（基于 Redux state）
 * 初始化所有配置的错误状态记录
 * 
 * @method clearErrorListener()
 * 清理错误监听
 * 清空所有错误状态记录
 * 
 * @method initOnceData()
 * 初始化只需加载一次的数据（组件挂载时执行）
 * 遍历 API_CONFIG.once 数组，执行所有只需加载一次的接口
 * 初始化失败不会弹窗，避免干扰用户
 * 
 * @method initData()
 * 初始化数据（首次加载）
 * 调用 fetchData 方法，不显示错误弹窗
 * 
 * @method setupVisibilityListener()
 * 设置页面可见性监听
 * 监听以下事件：
 * - visibilitychange：页面可见性变化
 * - focus：窗口获得焦点
 * - blur：窗口失去焦点
 * 
 * @method removeVisibilityListener()
 * 移除页面可见性监听
 * 移除所有页面可见性相关的事件监听器
 * 
 * @method handleVisibilityChange()
 * 处理页面可见性变化
 * 根据 document.hidden 状态调用 handlePageVisible 或 handlePageHidden
 * 
 * @method handlePageVisible()
 * 页面变为可见时的处理
 * 执行以下操作：
 * - 更新页面可见性状态
 * - 立即刷新数据
 * - 重新启动实时轮询
 * - 重新启动定时轮询
 * 
 * @method handlePageHidden()
 * 页面变为不可见时的处理
 * 执行以下操作：
 * - 更新页面可见性状态
 * - 暂停所有轮询（清除所有定时器）
 * 
 * @method startRealtimePolling()
 * 启动实时轮询（页面可见时持续轮询）
 * 遍历 API_CONFIG.realtime 数组，为每个接口创建定时器
 * 只有在页面可见时才会执行轮询
 * 默认轮询间隔为 30000 毫秒（30秒）
 * 
 * @method startPolling()
 * 启动定时轮询
 * 遍历 API_CONFIG.polling 数组，为每个接口创建定时器
 * 只有在页面可见时才会执行轮询
 * 默认轮询间隔为 30000 毫秒（30秒）
 * 
 * @method executeApiCall(config, showErrorDialog)
 * 执行单个接口调用
 * @param {Object} config - 接口配置对象，包含 name、dispatch、getParams 等属性
 * @param {Boolean} showErrorDialog - 是否显示错误弹窗，默认为 true
 * @returns {Promise} 返回接口调用的 Promise
 * 
 * @method handleApiError(config, error)
 * 处理接口错误（弹出确认框）
 * 显示 Modal.confirm 弹窗，提供重试和取消选项
 * 防止重复弹窗（通过 errorModalVisible 标志控制）
 * @param {Object} config - 接口配置对象
 * @param {Error} error - 错误对象
 * 
 * @method clearAllTimers()
 * 清除所有定时器
 * 遍历 timers 对象，清除所有定时器并重置 timers 对象
 * 
 * @method fetchData(value, showErrorDialog)
 * 获取数据（手动触发或初始化时调用）
 * 执行所有实时、定时和手动接口
 * 使用 Promise.allSettled 确保所有接口都会执行，即使有失败的
 * 如果有失败的接口且需要显示错误弹窗，只显示第一个失败接口的错误弹窗
 * @param {Object} value - 查询参数
 * @param {Boolean} showErrorDialog - 是否显示错误弹窗，默认为 true
 * @returns {Promise} 返回所有接口调用的 Promise
 * 
 * @example
 * // 在类组件中使用
 * class YourComponent extends React.Component {
 *   constructor(props) {
 *     super(props);
 *     this.apiManager = new ApiPollingManager(API_CONFIG, this);
 *   }
 * 
 *   componentDidMount() {
 *     this.apiManager.init(defaultQuery);
 *   }
 * 
 *   componentDidUpdate(prevProps) {
 *     this.apiManager.checkReduxErrors(prevProps, (props) => {
 *       return {
 *         fetchDBSummary: props.dbSummaryObj,
 *       };
 *     });
 *   }
 * 
 *   componentWillUnmount() {
 *     this.apiManager.cleanup();
 *   }
 * 
 *   updateQuery = (query) => {
 *     this.apiManager.updateQuery(query);
 *     this.apiManager.fetchData(query, true);
 *   }
 * }
 * 
 * @example
 * // API_CONFIG 配置格式
 * const API_CONFIG = {
 *   once: [
 *     {
 *       name: 'fetchStoreList',
 *       dispatch: fetchStoreList,
 *       getParams: () => ({ serviceType: 'default' })
 *     }
 *   ],
 *   realtime: [
 *     {
 *       name: 'fetchDBSummary',
 *       dispatch: fetchDBSummary,
 *       interval: 30000, // 30秒
 *       getParams: (query) => ({ query: { ...query } })
 *     }
 *   ],
 *   polling: [
 *     {
 *       name: 'fetchSomeData',
 *       dispatch: fetchSomeData,
 *       interval: 60000, // 60秒
 *       getParams: (query) => ({ query: { ...query } })
 *     }
 *   ],
 *   manual: [
 *     {
 *       name: 'fetchManualData',
 *       dispatch: fetchManualData,
 *       getParams: (query) => ({ query: { ...query } })
 *     }
 *   ]
 * }
 */
class ApiPollingManager {
  constructor(apiConfig, componentInstance) {
    this.apiConfig = apiConfig;
    this.component = componentInstance;
    this.timers = {}; // 存储所有定时器
    this.isPageVisible = true; // 页面可见性状态
    this.errorModalVisible = false; // 错误弹窗显示状态，防止重复弹窗
    this.lastErrors = {}; // 存储上次的错误状态
    this._inited = false; // 防止重复 init
    this._lastFetchAt = 0; // 上次 fetchData 时间戳（ms），用于防抖
    this._initAt = 0; // init 时间戳（ms），用于忽略初始化阶段的可见性抖动
  }

  /**
   * 初始化管理器（在 componentDidMount 中调用）
   * @param {Object} defaultQuery - 默认查询参数
   */
  init(defaultQuery = {}) {
    // 防止重复初始化（某些场景下组件可能重复触发 init）
    if (this._inited) {
      console.warn('[ApiPollingManager] init 已执行，跳过重复初始化')
      return
    }
    this._inited = true
    this._initAt = Date.now()
    this.currentQuery = defaultQuery;
    
    // 初始化只需加载一次的数据
    this.initOnceData();
    
    // 初始化数据
    this.initData();
    
    // 监听页面可见性变化
    this.setupVisibilityListener();
    
    // 启动实时轮询
    this.startRealtimePolling();
    
    // 启动定时轮询
    this.startPolling();
    
    // 设置错误监听
    this.setupErrorListener();
  }

  /**
   * 更新查询参数
   * @param {Object} query - 新的查询参数
   */
  updateQuery(query) {
    this.currentQuery = query;
  }

  /**
   * 清理管理器（在 componentWillUnmount 中调用）
   */
  cleanup() {
    // 清理所有定时器
    this.clearAllTimers();
    
    // 移除页面可见性监听
    this.removeVisibilityListener();
    
    // 清理错误监听
    this.clearErrorListener();

    // 允许下次重新初始化（组件卸载后）
    this._inited = false
    this._initAt = 0
  }

  /**
   * 检查 Redux state 中的错误（在 componentDidUpdate 中调用）
   * @param {Object} prevProps - 上一次的 props
   * @param {Function} getErrorProps - 获取错误 props 的函数，返回 { [configName]: errorObj }
   */
  checkReduxErrors(prevProps, getErrorProps) {
    if (!getErrorProps) return;
    
    const errorProps = getErrorProps(this.component.props);
    const prevErrorProps = getErrorProps(prevProps);
    
    // 检查所有配置中的错误
    const allConfigs = [
      ...this.apiConfig.realtime,
      ...this.apiConfig.polling,
      ...this.apiConfig.manual
    ];
    
    allConfigs.forEach(config => {
      const errorObj = errorProps[config.name];
      const prevErrorObj = prevErrorProps[config.name];
      
      // 检查是否有新的错误
      if (errorObj?.error && errorObj.error !== prevErrorObj?.error) {
        if (!this.errorModalVisible) {
          // console.log(`[checkReduxErrors] 检测到 ${config.name} 错误:`, errorObj.error);
          this.handleApiError(config, new Error(errorObj.error));
        }
      }
    });
  }

  /**
   * 设置错误监听（基于 Redux state）
   */
  setupErrorListener() {
    // 初始化错误状态记录
    const allConfigs = [
      ...this.apiConfig.realtime,
      ...this.apiConfig.polling,
      ...this.apiConfig.manual
    ];
    
    allConfigs.forEach(config => {
      this.lastErrors[config.name] = null;
    });
  }

  /**
   * 清理错误监听
   */
  clearErrorListener() {
    this.lastErrors = {};
  }

  /**
   * 初始化只需加载一次的数据（组件挂载时执行）
   */
  initOnceData = async () => {
    const { dispatch } = this.component.props;
    
    if (!this.apiConfig.once || this.apiConfig.once.length === 0) {
      return;
    }
    
    try {
      const promises = this.apiConfig.once.map(async (config) => {
        try {
          // 优先从 queryManager 获取最新的 query（如果组件有 queryManager）
          // 否则使用 currentQuery
          let query = this.currentQuery || {};
          if (this.component.queryManager && typeof this.component.queryManager.getQuery === 'function') {
            query = this.component.queryManager.getQuery();
          }
          
          // 传递 query 给 getParams，确保初始化时能获取到 query 参数
          const params = config.getParams ? config.getParams(query) : {};
          // console.log(`[${config.name}] 初始化（只需加载一次），参数:`, params, 'query:', query);
          const result = await dispatch(config.dispatch(params));
          
          // 检查返回的 action 类型，判断是否失败
          if (result && result.type && result.type.endsWith('/rejected')) {
            const errorMessage = result.payload || `${config.name} 初始化失败`;
            console.error(`[${config.name}] 初始化失败:`, result.payload);
            throw new Error(errorMessage);
          }
          
          // console.log(`[${config.name}] 初始化成功`);
          return result;
        } catch (error) {
          console.error(`[${config.name}] 初始化失败:`, error);
          // 初始化失败不弹窗，避免干扰用户
          throw error;
        }
      });
      
      await Promise.all(promises);
    } catch (error) {
      console.error('初始化只需加载一次的数据失败:', error);
      // 初始化失败不弹窗，避免干扰用户
    }
  }

  /**
   * 初始化数据（首次加载）
   */
  initData = async () => {
    try {
      this._lastFetchAt = Date.now()
      await this.fetchData(this.currentQuery, false); // false表示不显示错误弹窗
    } catch (error) {
      console.error('初始化数据失败:', error);
    }
  }

  /**
   * 设置页面可见性监听
   */
  setupVisibilityListener() {
    // 监听 visibilitychange 事件（页面标签页切换）
    // 注意：移除了 focus/blur 事件监听，因为：
    // 1. focus 事件会在用户点击页面空白处时触发，导致意外的数据重新获取
    // 2. visibilitychange 事件已经足够准确，能正确检测页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * 移除页面可见性监听
   */
  removeVisibilityListener() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * 处理页面可见性变化
   */
  handleVisibilityChange = () => {
    if (document.hidden) {
      this.handlePageHidden();
    } else {
      this.handlePageVisible();
    }
  }

  /**
   * 页面变为可见
   */
  handlePageVisible = () => {
    // 初始化阶段短时间内忽略可见性抖动（避免刷新时 blur->focus/visibilitychange 导致重复请求）
    const nowForInitGuard = Date.now()
    if (this._initAt && nowForInitGuard - this._initAt < 1500) {
      return
    }
    if (!this.isPageVisible) {
      this.isPageVisible = true;
      // 立即刷新数据
      // 注意：部分浏览器在刷新/切换标签页时会触发 blur->focus/visibilitychange，
      // 可能导致 initData 刚请求完又立刻请求一次；这里做一个轻量防抖（默认 800ms）
      const now = Date.now()
      const timeSinceLastFetch = now - (this._lastFetchAt || 0)
      if (timeSinceLastFetch > 800) {
        this._lastFetchAt = now
        this.fetchData(this.currentQuery, false);
      }
      // 重新启动轮询
      this.startRealtimePolling();
      this.startPolling();
    }
  }

  /**
   * 页面变为不可见
   */
  handlePageHidden = () => {
    // 初始化阶段短时间内忽略可见性抖动（避免状态翻转导致重复请求）
    const nowForInitGuard = Date.now()
    if (this._initAt && nowForInitGuard - this._initAt < 1500) {
      return
    }
    if (this.isPageVisible) {
      this.isPageVisible = false;
      // 暂停所有轮询
      this.clearAllTimers();
    }
  }

  /**
   * 启动实时轮询（页面可见时持续轮询）
   */
  startRealtimePolling() {
    if (!this.isPageVisible) return;
    
    if (!this.apiConfig.realtime || this.apiConfig.realtime.length === 0) {
      return;
    }
    
    this.apiConfig.realtime.forEach(config => {
      const timerKey = `realtime_${config.name}`;
      
      // 清除已存在的定时器
      if (this.timers[timerKey]) {
        clearInterval(this.timers[timerKey]);
      }
      
      // 创建新的定时器
      const timer = setInterval(() => {
        if (this.isPageVisible) {
          this.executeApiCall(config, true); // true表示需要错误处理
        }
      }, config.interval || 30000);
      
      this.timers[timerKey] = timer;
    });
  }

  /**
   * 启动定时轮询
   */
  startPolling() {
    if (!this.isPageVisible) return;
    
    if (!this.apiConfig.polling || this.apiConfig.polling.length === 0) {
      return;
    }
    
    this.apiConfig.polling.forEach(config => {
      const timerKey = `polling_${config.name}`;
      
      // 清除已存在的定时器
      if (this.timers[timerKey]) {
        clearInterval(this.timers[timerKey]);
      }
      
      // 创建新的定时器
      const timer = setInterval(() => {
        if (this.isPageVisible) {
          this.executeApiCall(config, true); // true表示需要错误处理
        }
      }, config.interval || 30000);
      
      this.timers[timerKey] = timer;
    });
  }

  /**
   * 执行单个接口调用
   */
  executeApiCall = async (config, showErrorDialog = true) => {
    const { dispatch } = this.component.props;
    try {
      const params = config.getParams ? config.getParams(this.currentQuery) : {};
      // console.log(`[${config.name}] 开始请求，参数:`, params);
      
      const result = await dispatch(config.dispatch(params));
      // console.log(`[${config.name}] 请求结果:`, result);
      
      // 检查返回的 action 类型，判断是否失败
      if (result && result.type && result.type.endsWith('/rejected')) {
        const errorMessage = result.payload || `${config.name} 请求失败`;
        console.error(`[${config.name}] 请求失败 (rejected):`, result.payload);
        if (showErrorDialog) {
          // console.log(`[${config.name}] 准备显示错误弹窗`);
          this.handleApiError(config, new Error(errorMessage));
        }
        return; // 提前返回，不继续执行
      }
      
      // console.log(`[${config.name}] 请求成功`);
    } catch (error) {
      // 如果 unwrap() 抛出错误，也会在这里捕获
      console.error(`[${config.name}] 请求失败 (catch):`, error);
      if (showErrorDialog) {
        // console.log(`[${config.name}] 准备显示错误弹窗 (catch)`);
        this.handleApiError(config, error);
      }
    }
  }

  /**
   * 处理接口错误（弹出确认框）
   */
  handleApiError = (config, error) => {
    // console.log(`[handleApiError] 开始处理错误，config:`, config.name, 'error:', error);
    
    // 防止重复弹窗
    if (this.errorModalVisible) {
      // console.log(`[handleApiError] 弹窗已显示，跳过`);
      return;
    }
    
    this.errorModalVisible = true;
    const errorMessage = error?.message || error?.toString() || `接口 ${config.name} 请求失败`;
    
    // console.log(`[handleApiError] 准备显示 Modal.confirm，错误信息:`, errorMessage);
    
    // try {
    //   Modal.confirm({
    //     title: '数据获取失败',
    //     icon: <ExclamationCircleOutlined />,
    //     content: `${errorMessage}，是否重试？`,
    //     okText: '重试',
    //     cancelText: '取消',
    //     onOk: () => {
    //       console.log(`[handleApiError] 用户点击重试`);
    //       this.errorModalVisible = false;
    //       // 用户确认重试
    //       this.executeApiCall(config, true);
    //     },
    //     onCancel: () => {
    //       console.log(`[handleApiError] 用户取消重试`);
    //       this.errorModalVisible = false;
    //     },
    //     afterClose: () => {
    //       console.log(`[handleApiError] Modal 关闭`);
    //       this.errorModalVisible = false;
    //     }
    //   });
    //   console.log(`[handleApiError] Modal.confirm 已调用`);
    // } catch (modalError) {
    //   console.error(`[handleApiError] Modal.confirm 调用失败:`, modalError);
    //   this.errorModalVisible = false;
    // }
  }

  /**
   * 清除所有定时器
   */
  clearAllTimers() {
    Object.values(this.timers).forEach(timer => {
      if (timer) clearInterval(timer);
    });
    this.timers = {};
  }

  /**
   * 获取数据（手动触发或初始化时调用）
   * @param {Object} value - 查询参数
   * @param {Boolean} showErrorDialog - 是否显示错误弹窗，默认true
   */
  fetchData = async (value, showErrorDialog = true) => {
    const { dispatch } = this.component.props;
    this.currentQuery = value; // 更新当前查询参数
    
    // 执行所有实时、定时和手动接口
    const allApis = [
      ...(this.apiConfig.realtime || []),
      ...(this.apiConfig.polling || []),
      ...(this.apiConfig.manual || [])
    ];
    
    if (allApis.length === 0) {
      return;
    }
    
    // 使用 Promise.allSettled 确保所有接口都会执行，即使有失败的
    const promises = allApis.map(async (config) => {
      try {
        const params = config.getParams ? config.getParams(value) : {};
        const result = await dispatch(config.dispatch(params));
        
        // 检查返回的 action 类型，判断是否失败
        if (result && result.type && result.type.endsWith('/rejected')) {
          const errorMessage = result.payload || `${config.name} 请求失败`;
          const error = new Error(errorMessage);
          console.error(`[${config.name}] 请求失败1:`, result.payload);
          return { success: false, config, error };
        }
        
        return { success: true, config, result };
      } catch (error) {
        console.error(`[${config.name}] 请求失败:`, error);
        return { success: false, config, error };
      }
    });
    
    // 等待所有接口完成（无论成功或失败）
    const results = await Promise.allSettled(promises);
    
    // 从结果中提取成功和失败的信息
    const successResults = results.filter(r => r.status === 'fulfilled' && r.value?.success);
    const failedResults = results.filter(r => 
      r.status === 'rejected' || (r.status === 'fulfilled' && !r.value?.success)
    );
    
    const successCount = successResults.length;
    const failCount = failedResults.length;
    
    // console.log(`[fetchData] 请求完成: 成功 ${successCount} 个，失败 ${failCount} 个`);
    
    // 如果有失败的接口且需要显示错误弹窗
    if (failCount > 0 && showErrorDialog && !this.errorModalVisible) {
      // 获取第一个失败接口的信息
      const firstFailed = failedResults[0];
      let failedInfo;
      
      if (firstFailed.status === 'fulfilled') {
        // Promise 成功但业务失败
        failedInfo = firstFailed.value;
      } else {
        // Promise 被 reject
        const failedIndex = results.findIndex(r => r === firstFailed);
        failedInfo = {
          config: allApis[failedIndex] || allApis[0],
          error: firstFailed.reason || new Error('请求失败')
        };
      }
      
      // 只显示第一个失败接口的错误弹窗，避免重复弹窗
      if (failedInfo && failedInfo.config) {
        this.handleApiError(failedInfo.config, failedInfo.error);
      }
    }
  }
}

export default ApiPollingManager;

